<script setup lang="ts">
import type { ProductMM } from '~/entities';
import {
  MMSlider,
  MMText,
  useMedia,
  type KeenSliderOptions,
  MMLink,
  MMIcon,
  MMSliderCss,
} from '@mm-frontend/mm-web-ui-kit';
import { computed, type ComputedRef } from 'vue';

withDefaults(
  defineProps<{ products: ProductMM[]; title: string; link?: string; visibleOverflow?: boolean; altTitle?: boolean }>(),
  {
    visibleOverflow: true,
  }
);

const { breakpoints: breakpointsMedia, isMobileOrTablet, screen } = useMedia();

const linkGoToTitle = computed(() => {
  return isMobileOrTablet.value ? 'Больше' : 'Показать больше';
});


const sliderOptions = computed<{ perView: number | 'auto'; gap: number }>(() => {
  switch (screen.value) {
    case 'Mobile':
    case 'Tablet':
      return {
        perView: 'auto',
        gap: 8,
      };
    case 'Laptop':
      return {
        perView: 4,
        gap: 20,
      };
    case 'Desktop':
    case 'DesktopMax':
      return {
        perView: 6,
        gap: 20,
      };
    default:
      return {
        perView: 'auto',
        gap: 7,
      };
  }
});
</script>

<template>
  <div
    v-if="products.length"
    :key="products.length"
    class="carousel-slides"
    :class="{ 'carousel-slides--visible-overflow': visibleOverflow }"
  >
    <div v-if="!altTitle" class="carousel-slides__header">
      <MMText
        font="Headline M/Bold"
        font-mobile-or-tablet="Body M/Medium"
        class="carousel-slides__title"
      >
        <MMLink v-if="link" :to="link" underline="hover" class="carousel-slides__title__link">
          <span class="carousel-slides__title__link__text">{{ title }}</span>
          <span class="carousel-slides__title__link__text__all">Все</span>
          <MMIcon
            name="chevron-bold"
            :width="isMobileOrTablet ? 24 : 36"
            :height="isMobileOrTablet ? 24 : 36"
            class="carousel-slides__title__link__icon"
          />
        </MMLink>

        <template v-else>{{ title }}</template>
      </MMText>
    </div>
    <div v-else class="carousel-slides__header" :style="{'font-family' : 'Magnit Box', color: '#473100'}">
      <MMText
        font="Headline L/Bold"
        font-mobile-or-tablet="Body M/Semibold"
        class="carousel-slides_ad__title"
        :style="{'font-family' : 'Magnit Box', color: '#473100'}"
      >
        {{ title }}
      </MMText>
      <MMLink :to="link" underline="hover" class="carousel-slides__title__link">
        <span class="carousel-slides__title__link__text">{{ linkGoToTitle }}</span>
        <MMIcon
          name="chevron-bold"
          :width="isMobileOrTablet ? 24 : 36"
          :height="isMobileOrTablet ? 24 : 36"
          class="carousel-slides__title__link__icon"
        />
      </MMLink>
    </div>

    <MMSliderCss
      :snap="false"
      :per-view="sliderOptions.perView"
      :gap="sliderOptions.gap"
      :controls="!isMobileOrTablet"
    >
      <div v-for="(product, index) in products" :key="product.product?.id" class="mm-slider__slide">
        <slot :product="product.product" :position="index" />
      </div>
    </MMSliderCss>
  </div>
</template>

<style lang="stylus" scoped>
.carousel-slides
  max-width 100%
  position relative

  --slider-horizontal-padding: 16px;

  &--visible-overflow
    +Media(Mobile, Tablet)
      margin-left calc(var(--slider-horizontal-padding) * -1)
      margin-right calc(var(--slider-horizontal-padding) * -1)
      width calc(100% + calc(var(--slider-horizontal-padding) * 2))
      max-width calc(100% + calc(var(--slider-horizontal-padding) * 2))

      & .carousel-slides__header
        padding-left var(--slider-horizontal-padding)
        padding-right var(--slider-horizontal-padding)

      :deep(.mm-slider-css__container)
        padding-left var(--slider-horizontal-padding)
        padding-right var(--slider-horizontal-padding)

  :deep(.mm-slider-css__container)
    border-radius 0

  &__header
    display flex
    justify-content space-between
    align-items center
    margin 21px 0

    +Media(Mobile, Tablet)
      margin 8px 0 12px
  &__title
    +Media(Mobile, Tablet)
      width 100%

    &__link
      &__text
        font-size 20px
        font-style normal
        font-weight 600
        line-height 105% /* 21px */
        &__all
          display none

      +Media(Mobile, Tablet)
        display flex
        width 100%

        &__icon
          margin-left -6px

        &__text
          font-size: 13px;
          &__all
            display block
            flex 1
            display flex
            justify-content flex-end

      &:hover
        color inherit!important
        text-decoration none!important

  & .mm-slider__slide
    height unset !important
    position relative

    +Media(Mobile)
      width 148px

    +Media(Tablet)
      width 171px

    // +Media(Laptop)

    // +Media(Desktop)

    // +Media(DesktopMax)
</style>
